/**
 * validations/hostname.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SKPLATFORM-UICC-001 CH-U1 — client-side domain validation for the Domains
 * panel.
 *
 * This is NOT the gate. skplatform-api's `hostname.ts` is, backed by a CHECK
 * constraint on `identity.org_hosts.host`. This exists so the person gets an
 * inline error while typing instead of a round trip, and so the message can
 * name what is actually wrong ("without the https://") rather than the API's
 * single generic code.
 *
 * ── The rule that matters ─────────────────────────────────────────────────
 * These rules must never be LOOSER than the API's. Looser means a value this
 * form accepts, the request rejects, and the person sees a generic failure on a
 * field that just told them it was fine. Stricter is merely annoying. The last
 * test in hostname.test.ts checks this validator against the DB's own regex so
 * the two repos cannot drift apart silently.
 */

/** RFC 1035 §2.3.4 — one label is at most 63 octets. */
const MAX_LABEL = 63;
/** 255 octets on the wire leaves 253 characters in dotted presentation form. */
const MAX_HOST = 253;

/** RFC 1123 label: alphanumeric at both ends, hyphens only in between. */
const LABEL = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Canonicalise spelling. Does not validate — `hostValidationError` judges.
 *
 * Lowercase because the API stores and matches lowercase; the trailing dot goes
 * because `example.com.` and `example.com` are the same host, and keeping both
 * would show the org two domains that route identical traffic.
 */
export function normaliseHost(raw: string): string {
  const trimmed = (raw ?? '').trim().toLowerCase();
  return trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed;
}

/**
 * The inline error for the Domains input, or null when the value is fine.
 *
 * Returns a MESSAGE rather than a boolean because CH-U1 renders it under the
 * field, and the specific cases (scheme, port, path) are the ones a person
 * pastes by accident — telling them "invalid domain" when they pasted a working
 * URL is the least useful thing the form could say.
 */
export function hostValidationError(raw: string): string | null {
  const value = (raw ?? '').trim();
  if (value.length === 0) return 'Enter a domain.';

  // Checked BEFORE the shape rules so the message can be specific. Each of
  // these is a thing people paste, not a typo.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value) || value.startsWith('//')) {
    return 'Enter just the domain, without the https:// prefix.';
  }
  if (value.includes('@')) return 'Enter just the domain, without an email address or credentials.';
  if (/:\d+$/.test(value)) return 'Enter just the domain, without a port.';
  if (value.includes('/')) return 'Enter just the domain, without a path.';
  if (value.includes('?') || value.includes('#')) {
    return 'Enter just the domain, without a query string.';
  }
  if (value.includes('*')) return 'Wildcard domains are not supported. Add each domain separately.';

  const host = normaliseHost(value);

  if (host.length > MAX_HOST) return 'That domain is too long.';

  const labels = host.split('.');
  // A single label ('localhost') is not an address a rider's browser can reach
  // this platform on; accepting one would save a binding that can never resolve.
  if (labels.length < 2) return 'Enter a full domain, for example taxis.urbanway.com.';

  for (const label of labels) {
    if (label.length === 0) return 'That domain has an empty part — check the dots.';
    if (label.length > MAX_LABEL) return 'One part of that domain is too long.';
    if (!LABEL.test(label)) {
      return 'A domain can contain only letters, numbers and hyphens, and cannot start or end a part with a hyphen.';
    }
  }

  // An all-numeric final label is how an IPv4 literal passes every rule above.
  if (/^[0-9]+$/.test(labels[labels.length - 1])) {
    return 'Enter a domain name, not an IP address.';
  }

  return null;
}
